import { ReactNode } from "react";
import { IconName } from "@ui/atoms/Icon/registry";
import { MetricTileVariant } from "@themes/tokens/metricTile";
import { FormattingKind } from "@utils/metricFormat";

/**
 * How the bottom progress bar behaves:
 * - `none`        — no bar at all.
 * - `decorative`  — full main-color bar, revealed only on hover/active. Carries
 *                   no data; keeps non-progress cards visually consistent.
 * - `interactive` — real `value`/`progressValue`, revealed on hover/active.
 *                   For clickable filter cards (e.g. project reports).
 * - `persistent`  — real `value`/`progressValue`, always visible. For read-only
 *                   data tiles (e.g. analytics) where the bar is information, so
 *                   touch/keyboard users must see it without hovering.
 */
export type MetricCardProgressBar =
  | "none"
  | "decorative"
  | "interactive"
  | "persistent";

/** Tone of the optional delta line — drives its colour. */
export type MetricCardDeltaTone = "positive" | "negative" | "neutral";

/** Alias of the shared tile scale — MetricCard does not define its own sizes. */
export type MetricCardVariant = MetricTileVariant;

/**
 * A number to show on a card, named by what it *is* rather than by how to
 * print it.
 *
 * The card derives every affix and decimal place from `kind` via the shared
 * standard (`@utils/metricFormat`), so the same figure reads identically on
 * every tile in the app. There is deliberately no `prefix`, `suffix` or
 * `decimalPlaces` here: those let a call site restate the formatting decision,
 * and 45 of them restated it differently — the abbreviated `$1.9m` on one
 * screen against a literal `$1,891,980` on the next.
 */
interface MetricCardValueBase {
  /**
   * A trailing qualifier the standard does not own — a unit (`"hr"`,
   * `"tCO₂e"`, `"kWh"`) or a short phrase (`"of target"`). Rendered after the
   * value, spaced and de-emphasised. Money and percentages carry their own
   * symbol and must not repeat it here.
   */
  unit?: string;
  valueColor?: string;
}

export interface MetricCardNumericValue extends MetricCardValueBase {
  kind: FormattingKind;
  value: number;
}

/**
 * A value that is not a number — an item name, a package, a date range. Nothing
 * to format or animate, so it renders verbatim.
 *
 * Not an escape hatch for numbers: a figure formatted into a string here would
 * be exactly the bypass `kind` exists to prevent.
 */
export interface MetricCardTextValue extends MetricCardValueBase {
  string: string;
  /**
   * Render at the smaller step on the tile scale (`valueString`).
   *
   * Opt-in, not automatic for every text value. A short code reads as a figure
   * and belongs at the figure size — a postcode shrunk to fit prose looks
   * broken beside the counts either side of it. A NAME is what needs the
   * smaller step: it runs to several words, wraps, and at the figure size
   * stands taller than every tile around it.
   */
  wraps?: boolean;
}

export type MetricCardValue = MetricCardNumericValue | MetricCardTextValue;

/** Narrow a {@link MetricCardValue} to its non-numeric form. */
export const isTextValue = (
  value: MetricCardValue
): value is MetricCardTextValue => "string" in value;

export interface MetricCardChip {
  text?: string;
  color?: string;
  icon?: React.ReactElement;
  tooltip?: string;
}

export interface MetricCardDelta {
  /** Delta text, e.g. "+0.1% vs May". */
  text?: string;
  /** Colour of the delta line; defaults to neutral. */
  tone?: MetricCardDeltaTone;
  /**
   * Which way the figure moved. Renders a trend glyph before {@link text}:
   * `"up"` → `↗ +5 This Period`, `"down"` → `↘ -5 This Period`.
   *
   * Deliberately independent of {@link tone}. Direction is arithmetic — the
   * sign of the number, and {@link trendDirection} is the one place that reads
   * it. Whether a rise is *good* is a domain judgement that inverts between
   * surfaces (workforce up is good; emissions up is not), so the glyph makes
   * no claim about it and the line stays neutral unless a caller says
   * otherwise.
   */
  direction?: "up" | "down";
}

/**
 * The {@link MetricCardDelta.direction} for a signed figure. Zero counts as
 * "up" so a flat period reads as level rather than as a fall.
 */
export const trendDirection = (value: number): "up" | "down" =>
  value < 0 ? "down" : "up";

export interface MetricCardCallout {
  label?: string;
  text: string;
  icon?: IconName;
}

export interface MetricCardProps {
  tooltipText?: ReactNode | string;
  label: string;
  subtitle?: string;
  accentColor?: string;
  isActive?: boolean;
  onClick?: () => void;
  showLeftBorder?: boolean;
  progressBar?: MetricCardProgressBar;
  progressValue?: number;
  dataTestId?: string;
  showDot?: boolean;
  loading?: boolean;
  minHeight?: number;
  variant?: MetricCardVariant;
  // grouped
  valueConfig?: MetricCardValue;
  chip?: MetricCardChip;
  delta?: MetricCardDelta;
  callout?: MetricCardCallout;
}
